import TextField from "@material-ui/core/TextField";
import classnames from "classnames";
import { History } from "history";
import { all } from "ramda";
import * as React from "react";
import { connect } from "react-redux";
import { isRequesting } from "src/requestProgress/helpers";
import { getRequestProgress } from "src/requestProgress/selector";
import { deleteCachedSearch } from "src/searchCache/actions";
import { showAlertBanner } from "src/shared/alertBanner/actions";
import { ALERT_BANNER_TYPES } from "src/shared/alertBanner/reducer";
import QuiqButton from "src/shared/QuiqButton";
import selectorsToProps from "src/utils/selectorsToProps";
import { actions, RequestProgressState } from "../requestProgress/reducer";
import { isUserAuthenticated } from "../route/selector";
import {
  expireSession,
  ExpireSessionActionType,
  resetPassword,
  ResetPasswordActionType,
  resetUser
} from "./actions";
import { EmptyGrayCircleIcon } from "./EmptyGrayCircleIcon";
import { GreenCircleCheckIcon } from "./GreenCircleCheckIcon";
import "./LoggedOutForm.scss";
import LoggedOutView from "./LoggedOutView";

interface ResetPasswordProps {
  history: History;
  showAlertBanner: typeof showAlertBanner;
  resetPassword: ResetPasswordActionType;
  requestProgress: RequestProgressState;
  isUserAuthenticated: boolean;
  expireSession: ExpireSessionActionType;
  resetUser: typeof resetUser;
  deleteCachedSearchFunc: typeof deleteCachedSearch;
}

interface ResetPasswordState {
  password: string;
  passwordConfirmation: string;
  passwordErrorText: string;
  confirmationCode: string;
  username: string;
  hasCapitalLetter: boolean;
  hasAtLeast8Chars: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
  passwordConfirmationError: boolean;
  hasLowercaseLetter: boolean;
}

export const hasAtLeast8Chars = (password: string): boolean => {
  const hasAtLeast8CharactersMatcher = /.{8}/;

  const matchers = [hasAtLeast8CharactersMatcher];

  return all((regex) => regex.test(password), matchers);
};

export const hasSpecialChar = (password: string): boolean => {
  const hasOnlyAlphanumericCharactersMatcher = /^[A-Za-z0-9\s]+$/;

  const matchers = [hasOnlyAlphanumericCharactersMatcher];

  return !all((regex) => regex.test(password), matchers) && password.length > 0;
};

export const hasCapitalLetter = (password: string): boolean => {
  const hasCapitalLetterMatcher = /[A-Z]/;

  const matchers = [hasCapitalLetterMatcher];

  return all((regex) => regex.test(password), matchers);
};

export const hasLowercaseLetter = (password: string): boolean => {
  const hasLowercaseLetterMatcher = /[a-z]/;

  const matchers = [hasLowercaseLetterMatcher];

  return all((regex) => regex.test(password), matchers);
};

export const hasNumber = (password: string): boolean => {
  const hasNumberMatcher = /[0-9]/;

  const matchers = [hasNumberMatcher];

  return all((regex) => regex.test(password), matchers);
};

export const validPassword = (password: string): boolean => {
  return (
    hasNumber(password) &&
    hasAtLeast8Chars(password) &&
    hasSpecialChar(password) &&
    hasCapitalLetter(password) &&
    hasLowercaseLetter(password)
  );
};

export class ResetPassword extends React.Component<
  ResetPasswordProps,
  ResetPasswordState
> {
  constructor(props: ResetPasswordProps) {
    super(props);
    this.state = {
      password: "",
      passwordConfirmation: "",
      passwordErrorText: "",
      confirmationCode: "",
      username: "",
      hasCapitalLetter: false,
      hasAtLeast8Chars: false,
      hasNumber: false,
      hasSpecialChar: false,
      passwordConfirmationError: false,
      hasLowercaseLetter: false
    };

    this.goToLogin = this.goToLogin.bind(this);
  }

  public async componentDidMount() {
    const queryparams = new URLSearchParams(this.props.history.location.search);
    const code = queryparams.get("code") || "";
    const user = queryparams.get("user") || "";
    this.setState({
      username: user,
      confirmationCode: code
    });
    if (this.props.isUserAuthenticated) {
      await this.props.expireSession();
      this.props.deleteCachedSearchFunc({});
      this.props.resetUser({ errorMessage: "" });
    }
  }

  public handleChangePassword = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const password = event.target.value;
    this.setState({
      hasAtLeast8Chars: hasAtLeast8Chars(password),
      hasCapitalLetter: hasCapitalLetter(password),
      hasSpecialChar: hasSpecialChar(password),
      hasNumber: hasNumber(password),
      hasLowercaseLetter: hasLowercaseLetter(password),
      passwordErrorText: "",
      passwordConfirmationError: false
    });

    this.setState({
      [event.target.name]: event.target.value
    } as any);
  };

  public handleChangePasswordConfirmation = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    this.setState({
      [event.target.name]: event.target.value
    } as any);

    this.setState({
      passwordErrorText: "",
      passwordConfirmationError: false
    });
  };

  public handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    const { password, confirmationCode, username } = this.state;
    this.setState({
      passwordConfirmationError: this.passwordConfirmationError()
    });
    if (!password) {
      this.setState({ passwordErrorText: "Password is required" });
    } else if (this.passwordConfirmationError()) {
      this.setState({ passwordConfirmationError: true });
    } else if (!this.isSubmittable()) {
      this.setState({
        passwordErrorText: "Password does not meet the requirements"
      });
    } else if (username && confirmationCode) {
      this.props
        .resetPassword({ username, password, confirmationCode })
        .then((result) => {
          if (result.passwordChanged) {
            this.props.showAlertBanner({
              type: ALERT_BANNER_TYPES.SUCCESS,
              message: "Success - Password Saved"
            });
            this.goToLogin();
          } else {
            this.setState({
              passwordErrorText: result.errorText || ""
            });
            this.props.showAlertBanner({
              type: ALERT_BANNER_TYPES.ERROR,
              message: "Error - Could Not Save Password"
            });
          }
        })
        .catch((error) => {
          this.setState({
            passwordErrorText: error.toString()
          });
          this.props.showAlertBanner({
            type: ALERT_BANNER_TYPES.ERROR,
            message: "Error - Could Not Save Password"
          });
        });
    }
    event.preventDefault();
  };

  public goToLogin() {
    this.props.history.push("/login");
  }

  public render() {
    return (
      <div>
        <LoggedOutView>
          {{
            header: "Set Your Password",
            prompt: "Make sure to include at least:",
            content: (
              <div>
                {
                  <form onSubmit={this.handleSubmit}>
                    <div className="LoggedOutForm__password__requirements">
                      {this.state.hasAtLeast8Chars ? (
                        <GreenCircleCheckIcon />
                      ) : (
                        <EmptyGrayCircleIcon />
                      )}
                      8 characters
                    </div>
                    <div className="LoggedOutForm__password__requirements">
                      {this.state.hasCapitalLetter ? (
                        <GreenCircleCheckIcon />
                      ) : (
                        <EmptyGrayCircleIcon />
                      )}
                      1 uppercase letter (A-Z)
                    </div>
                    <div className="LoggedOutForm__password__requirements">
                      {this.state.hasLowercaseLetter ? (
                        <GreenCircleCheckIcon />
                      ) : (
                        <EmptyGrayCircleIcon />
                      )}
                      1 lowercase letter (a-z)
                    </div>
                    <div className="LoggedOutForm__password__requirements">
                      {this.state.hasSpecialChar ? (
                        <GreenCircleCheckIcon />
                      ) : (
                        <EmptyGrayCircleIcon />
                      )}
                      1 special character
                    </div>
                    <div className="LoggedOutForm__password__requirements">
                      {this.state.hasNumber ? (
                        <GreenCircleCheckIcon />
                      ) : (
                        <EmptyGrayCircleIcon />
                      )}
                      1 number (0-9)
                    </div>
                    <div className="LoggedOutForm__field">
                      <TextField
                        type="password"
                        value={this.state.password}
                        onChange={this.handleChangePassword}
                        name="password"
                        id="password"
                        variant="outlined"
                        label="Password"
                        helperText={this.state.passwordErrorText || ""}
                        error={this.state.passwordErrorText !== ""}
                        className="LoggedOutForm__field__password-input"
                        InputProps={{
                          classes: {
                            focused: "TextInput__container--focused",
                            notchedOutline: "TextInput__outline"
                          } as any
                        }}
                        InputLabelProps={{
                          classes: {
                            root: "TextInput__label",
                            shrink: "TextInput__label--shrink",
                            focused: "TextInput__label--focused"
                          }
                        }}
                      />
                    </div>

                    <div className="LoggedOutForm__field">
                      <TextField
                        type="password"
                        value={this.state.passwordConfirmation}
                        onChange={this.handleChangePasswordConfirmation}
                        name="passwordConfirmation"
                        id="password-confirmation"
                        variant="outlined"
                        label="Confirm Password"
                        error={this.state.passwordConfirmationError}
                        helperText={
                          this.state.passwordConfirmationError &&
                          "Passwords do not match"
                        }
                        InputProps={{
                          classes: {
                            focused: "TextInput__container--focused",
                            notchedOutline: !this.state
                              .passwordConfirmationError
                              ? "TextInput__outline"
                              : ""
                          } as any
                        }}
                        InputLabelProps={{
                          classes: {
                            root: "TextInput__label",
                            shrink: !this.state.passwordConfirmationError
                              ? "TextInput__label--shrink"
                              : "",
                            focused: "TextInput__label--focused"
                          }
                        }}
                        className="LoggedOutForm__field__password-input"
                      />
                    </div>

                    <div className="LoggedOutForm__button-row">
                      <QuiqButton
                        type="submit"
                        color="blue"
                        isLoading={isRequesting(actions.resetPassword)}
                        className={this.submitButtonClasses()}
                      >
                        SET PASSWORD
                      </QuiqButton>
                    </div>
                  </form>
                }
              </div>
            )
          }}
        </LoggedOutView>
      </div>
    );
  }

  private submitButtonClasses = (): string => {
    return classnames("SubmitButton", {
      SubmitButton__ready: this.isSubmittable()
    });
  };

  private isSubmittable = (): boolean => {
    return (
      this.state.password.length > 0 &&
      this.state.passwordConfirmation.length > 0 &&
      validPassword(this.state.password)
    );
  };

  private passwordConfirmationError() {
    return this.state.password !== this.state.passwordConfirmation;
  }
}

export default connect(
  selectorsToProps({
    requestProgress: getRequestProgress,
    isUserAuthenticated
  }),
  {
    showAlertBanner,
    resetPassword: resetPassword.action,
    expireSession: expireSession.action,
    deleteCachedSearchFunc: deleteCachedSearch,
    resetUser
  }
)(ResetPassword);



// WEBPACK FOOTER //
// ./src/login/ResetPassword.tsx